/**
 * Utility functions for calculating trip segments and distances with via cities
 */

/**
 * Calculate total distance by summing all segments of a route with via cities
 * @param {string} startLocation - Starting city
 * @param {string} destination - Final destination
 * @param {Array<string>} viaCities - Array of via cities in order
 * @param {number} fallbackDistance - Fallback distance if calculation fails
 * @returns {Object} - Object containing segment details and total distance
 */
export const calculateRouteSegments = (
  startLocation,
  destination,
  viaCities = [],
  fallbackDistance = 0,
) => {
  const segments = [];
  const viaCityNames = (viaCities || []).map(city => 
    typeof city === 'string' ? city : (city?.name || city?.address || '')
  ).filter(Boolean);
  const cities = [startLocation, ...viaCityNames, destination].filter(
    Boolean,
  );

  // If we have less than 2 cities, return simple route
  if (cities.length < 2) {
    return {
      segments: [
        {
          from: startLocation || "Origin",
          to: destination || "Destination",
          distance: fallbackDistance,
        },
      ],
      totalDistance: fallbackDistance,
      routePath: cities,
    };
  }

  // Create segments between consecutive cities
  for (let i = 0; i < cities.length - 1; i++) {
    segments.push({
      from: cities[i],
      to: cities[i + 1],
      distance: estimateSegmentDistance(
        cities[i],
        cities[i + 1],
        fallbackDistance,
        cities.length - 1,
      ),
      isViaSegment: i < cities.length - 2, // All segments except the last one are via segments
    });
  }

  // Calculate total distance
  const totalDistance = segments.reduce(
    (sum, segment) => sum + segment.distance,
    0,
  );

  return {
    segments,
    totalDistance: totalDistance || fallbackDistance,
    routePath: cities,
  };
};

/**
 * Estimate distance for a segment between two cities
 * @param {string} from - Starting city
 * @param {string} to - Destination city
 * @param {number} totalDistance - Total trip distance
 * @param {number} totalSegments - Total number of segments
 * @returns {number} - Estimated segment distance
 */
const estimateSegmentDistance = (from, to, totalDistance, totalSegments) => {
  // If we have total distance, distribute it equally among segments
  if (totalDistance && totalSegments > 0) {
    return Math.round(totalDistance / totalSegments);
  }

  // Fallback: estimate based on city names (very basic)
  // This is a placeholder - in a real app, you'd use Google Maps Distance Matrix API
  return estimateDistanceByCityNames(from, to);
};

/**
 * Basic estimation of distance between cities based on names
 * This is a placeholder function - replace with actual distance calculation
 * @param {string} from - Starting city
 * @param {string} to - Destination city
 * @returns {number} - Estimated distance in km
 */
const estimateDistanceByCityNames = (from, to) => {
  if (!from || !to) return 50; // Default 50km

  // Very basic estimation based on string similarity
  // In a real application, you would use Google Maps Distance Matrix API
  const fromLower = from.toLowerCase();
  const toLower = to.toLowerCase();

  // If same city/location
  if (fromLower === toLower) return 0;

  // Basic heuristics for common Indian cities (this is just for demo)
  const cityDistances = {
    "mumbai-delhi": 1150,
    "delhi-mumbai": 1150,
    "mumbai-bangalore": 840,
    "bangalore-mumbai": 840,
    "delhi-bangalore": 1740,
    "bangalore-delhi": 1740,
    "mumbai-pune": 150,
    "pune-mumbai": 150,
    "delhi-jaipur": 280,
    "jaipur-delhi": 280,
    "mumbai-ahmedabad": 525,
    "ahmedabad-mumbai": 525,
    "delhi-chandigarh": 245,
    "chandigarh-delhi": 245,
    "bangalore-chennai": 350,
    "chennai-bangalore": 350,
    "mumbai-goa": 465,
    "goa-mumbai": 465,
    "delhi-lucknow": 555,
    "lucknow-delhi": 555,
    "rajkot-dehradun": 1383,
    "dehradun-rajkot": 1383,
    "rajkot-mumbai": 450,
    "mumbai-surat": 264,
    "surat-dehradun": 669,
    "rajkot-ahmedabad": 220,
    "ahmedabad-rajkot": 220,
    "rajkot-surat": 330,
    "surat-rajkot": 330,
    "ahmedabad-surat": 285,
    "surat-ahmedabad": 285,
    "mumbai-rajkot": 450,
    "mumbai-ahmedabad": 525,
    "ahmedabad-delhi": 820,
    "delhi-ahmedabad": 820,
    "surat-delhi": 1100,
    "delhi-surat": 1100,
  };

  // Check for exact match
  const routeKey = `${fromLower}-${toLower}`;
  if (cityDistances[routeKey]) {
    return cityDistances[routeKey];
  }

  // Check if cities contain known city names
  for (const [route, distance] of Object.entries(cityDistances)) {
    const [city1, city2] = route.split("-");
    if (
      (fromLower.includes(city1) && toLower.includes(city2)) ||
      (fromLower.includes(city2) && toLower.includes(city1))
    ) {
      return distance;
    }
  }

  // Default estimation based on character difference (very rough)
  const charDiff = Math.abs(from.length - to.length);
  return Math.max(100, charDiff * 50); // Minimum 100km, increase based on name difference
};

/**
 * Calculate fare breakdown for each segment and total
 * @param {Array} segments - Array of route segments
 * @param {number} baseRate - Base rate per km
 * @param {Object} options - Additional pricing options
 * @returns {Object} - Detailed fare breakdown
 */
export const calculateSegmentFares = (
  segments,
  baseRate = 12,
  options = {},
) => {
  const {
    tollCharges = 0,
    driverAllowance = 0,
    nightCharges = 0,
    taxRate = 0.18,
  } = options;

  const segmentFares = segments.map((segment) => {
    const segmentFare = segment.distance * baseRate;
    return {
      ...segment,
      fare: Math.round(segmentFare),
      ratePerKm: baseRate,
    };
  });

  const subtotal = segmentFares.reduce((sum, segment) => sum + segment.fare, 0);
  const taxes = Math.round(subtotal * taxRate);
  const totalAdditionalCharges = tollCharges + driverAllowance + nightCharges;
  const totalFare = subtotal + taxes + totalAdditionalCharges;

  return {
    segments: segmentFares,
    breakdown: {
      subtotal,
      taxes,
      tollCharges,
      driverAllowance,
      nightCharges,
      totalAdditionalCharges,
      totalFare,
    },
    totalDistance: segmentFares.reduce(
      (sum, segment) => sum + segment.distance,
      0,
    ),
    baseRate,
  };
};

/**
 * Format route display with via cities
 * @param {string} startLocation - Starting location
 * @param {string} destination - Final destination
 * @param {Array<string>} viaCities - Array of via cities
 * @param {boolean} compact - Whether to use compact format
 * @returns {string} - Formatted route string
 */
export const formatRouteDisplay = (
  startLocation,
  destination,
  viaCities = [],
  compact = false,
) => {
  if (!startLocation || !destination) return "Route not specified";

  const viaCityNames = (viaCities || []).map(city => 
    typeof city === 'string' ? city : (city?.name || city?.address || '')
  ).filter(Boolean);

  if (!viaCityNames || viaCityNames.length === 0) {
    return `${startLocation} → ${destination}`;
  }

  if (compact) {
    return `${startLocation} → ${destination} (+${viaCityNames.length} via)`;
  }

  const allCities = [startLocation, ...viaCityNames, destination];
  return allCities.join(" → ");
};

/**
 * Extract city name from full address
 * @param {string} address - Full address string
 * @returns {string} - Extracted city name
 */
export const extractCityName = (address) => {
  if (!address || typeof address !== "string") return "";

  // Split by comma and take the first part (usually city)
  const parts = address.split(",").map((part) => part.trim());

  // Return the first non-empty part
  return parts.find((part) => part.length > 0) || address;
};
